import { useEffect, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { LegalKnowledge } from '@/mocks/systemData';
import { useAuth } from '@/context/AuthContext';

const API_BASE =
  'https://guardiancloudcare.com/backend/api';

const legalTypeColors: Record<
  string,
  string
> = {
  law:
    'bg-red-100 text-red-700',
  guideline:
    'bg-teal-100 text-teal-700',
  policy:
    'bg-amber-100 text-amber-700',
};

const caseTypeOptions = [
  'Physical Abuse',
  'Sexual Abuse',
  'Emotional Abuse',
  'Neglect',
  'Child Labor',
  'Child Trafficking',
  'Domestic Violence',
  'Online Sexual Exploitation',
];

const emptyLegalForm = {
  title: '',
  code: '',
  type:
    'law' as LegalKnowledge['type'],
  description: '',
  caseTypes: [] as string[],
};

export default function AdminLegalKnowledgePage() {
  const [legalList, setLegalList] =
    useState<LegalKnowledge[]>([]);

  const [
    showLegalModal,
    setShowLegalModal,
  ] = useState(false);

  const [legalForm, setLegalForm] =
    useState(emptyLegalForm);

  const [
    editingId,
    setEditingId,
  ] = useState<number | null>(null);

  const [
    legalSaved,
    setLegalSaved,
  ] = useState(false);

  const [
    filterType,
    setFilterType,
  ] = useState<
    'all' | LegalKnowledge['type']
  >('all');

  const [search, setSearch] =
    useState('');

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const {
    user: currentUser,
  } = useAuth();

  /*
   * ========================================================
   * SUCCESS MESSAGE
   * ========================================================
   */

  const showSavedMessage = () => {
    setLegalSaved(true);

    window.setTimeout(() => {
      setLegalSaved(false);
    }, 2500);
  };

  /*
   * ========================================================
   * RESET FORM
   * ========================================================
   */

  const resetForm = () => {
    setLegalForm({
      ...emptyLegalForm,
      caseTypes: [],
    });

    setEditingId(null);
  };

  /*
   * ========================================================
   * LOAD DATA
   * ========================================================
   */

  const loadLegalKnowledge =
    async () => {
      try {
        setLoading(true);

        const response =
          await fetch(
            `${API_BASE}/get_legal_knowledge.php`
          );

        if (!response.ok) {
          throw new Error(
            `HTTP error ${response.status}`
          );
        }

        const result =
          await response.json();

        if (!result.success) {
          throw new Error(
            result.error ||
              'Failed to load legal knowledge'
          );
        }

        const records =
          Array.isArray(
            result.data
          )
            ? result.data
            : [];

        const formattedRecords:
          LegalKnowledge[] =
          records
            .map(
              (
                record: any
              ) => {
                /*
                 * Database ID MUST be a positive integer.
                 */
                const id =
                  Number(
                    record.id
                  );

                if (
                  !Number.isInteger(
                    id
                  ) ||
                  id <= 0
                ) {
                  console.warn(
                    'Invalid legal knowledge ID. Record skipped:',
                    record
                  );

                  return null;
                }

                /*
                 * Case types.
                 */
                let caseTypes:
                  string[] = [];

                if (
                  Array.isArray(
                    record.case_types
                  )
                ) {
                  caseTypes =
                    record.case_types.filter(
                      (
                        value: unknown
                      ): value is string =>
                        typeof value ===
                          'string' &&
                        value.trim() !==
                          ''
                    );
                } else if (
                  typeof record.case_types ===
                  'string'
                ) {
                  try {
                    const parsed =
                      JSON.parse(
                        record.case_types
                      );

                    if (
                      Array.isArray(
                        parsed
                      )
                    ) {
                      caseTypes =
                        parsed.filter(
                          (
                            value: unknown
                          ): value is string =>
                            typeof value ===
                              'string' &&
                            value.trim() !==
                              ''
                        );
                    }
                  } catch {
                    caseTypes =
                      record.case_types.trim()
                        ? [
                            record.case_types.trim(),
                          ]
                        : [];
                  }
                }

                return {
                  id,

                  title:
                    record.document_name ||
                    record.title ||
                    '',

                  code:
                    record.legal_code ||
                    record.code ||
                    '',

                  type:
                    record.legal_type ===
                    'guideline'
                      ? 'guideline'
                      : record.legal_type ===
                        'policy'
                      ? 'policy'
                      : 'law',

                  description:
                    record.description ||
                    '',

                  caseTypes,

                  uploadedAt:
                    record.uploaded_at
                      ? String(
                          record.uploaded_at
                        ).split(
                          ' '
                        )[0]
                      : '',

                  status:
                    record.status ===
                    'draft'
                      ? 'draft'
                      : 'active',
                };
              }
            )
            .filter(
              (
                item
              ): item is LegalKnowledge =>
                item !== null
            );

        /*
         * Ensure no duplicate IDs.
         */
        const uniqueRecords =
          Array.from(
            new Map(
              formattedRecords.map(
                (item) => [
                  item.id,
                  item,
                ]
              )
            ).values()
          );

        setLegalList(
          uniqueRecords
        );

      } catch (error) {
        console.error(
          'Failed to load legal knowledge:',
          error
        );

        setLegalList([]);

        alert(
          error instanceof Error
            ? error.message
            : 'Failed to load legal knowledge.'
        );

      } finally {
        setLoading(false);
      }
    };

  /*
   * ========================================================
   * INITIAL LOAD
   * ========================================================
   */

  useEffect(() => {
    loadLegalKnowledge();
  }, []);

  /*
   * ========================================================
   * AUDIT LOG
   * ========================================================
   */

  const createAuditLog =
    async (
      action: string,
      module: string,
      description: string
    ) => {
      try {
        await fetch(
          `${API_BASE}/create_audit_log.php`,
          {
            method: 'POST',
            headers: {
              'Content-Type':
                'application/json',
            },
            body: JSON.stringify({
              uid:
                currentUser?.uid ||
                '',

              user_name:
                currentUser?.name ||
                '',

              user_role:
                currentUser?.role ||
                '',

              action,
              module,
              description,
            }),
          }
        );

      } catch (error) {
        console.error(
          'Failed to create audit log:',
          error
        );
      }
    };

  /*
   * ========================================================
   * ADD MODAL
   * ========================================================
   */

  const openAddModal = () => {
    resetForm();
    setShowLegalModal(true);
  };

  /*
   * ========================================================
   * EDIT MODAL
   * ========================================================
   */

  const openEditModal = (
    document: LegalKnowledge
  ) => {
    const id =
      Number(document.id);

    if (
      !Number.isInteger(id) ||
      id <= 0
    ) {
      alert(
        'Invalid database ID. Please refresh the page.'
      );
      return;
    }

    setEditingId(id);

    setLegalForm({
      title:
        document.title || '',

      code:
        document.code || '',

      type:
        document.type || 'law',

      description:
        document.description || '',

      caseTypes:
        Array.isArray(
          document.caseTypes
        )
          ? [
              ...document.caseTypes,
            ]
          : [],
    });

    setShowLegalModal(true);
  };

  /*
   * ========================================================
   * ADD
   * ========================================================
   */

  const handleAddLegal =
    async () => {
      if (
        !legalForm.title.trim() ||
        !legalForm.code.trim()
      ) {
        alert(
          'Title and legal code are required.'
        );
        return;
      }

      if (
        !legalForm.description.trim()
      ) {
        alert(
          'Description is required.'
        );
        return;
      }

      try {
        setSaving(true);

        const response =
          await fetch(
            `${API_BASE}/create_legal_knowledge.php`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body: JSON.stringify({
                document_name:
                  legalForm.title.trim(),

                legal_code:
                  legalForm.code.trim(),

                legal_type:
                  legalForm.type,

                description:
                  legalForm.description.trim(),

                case_types:
                  legalForm.caseTypes,

                status:
                  'active',

                uploaded_by:
                  currentUser?.uid ||
                  '',
              }),
            }
          );

        const result =
          await response.json();

        if (
          !response.ok ||
          !result.success
        ) {
          throw new Error(
            result.error ||
              'Failed to create legal knowledge'
          );
        }

        await createAuditLog(
          'Create',
          'Legal Knowledge',
          `Created legal document "${legalForm.title.trim()}" (${legalForm.code.trim()})`
        );

        setShowLegalModal(
          false
        );

        resetForm();

        await loadLegalKnowledge();

        showSavedMessage();

      } catch (error) {
        console.error(
          'Failed to create legal knowledge:',
          error
        );

        alert(
          error instanceof Error
            ? error.message
            : 'Failed to save legal knowledge.'
        );

      } finally {
        setSaving(false);
      }
    };

  /*
   * ========================================================
   * UPDATE
   * ========================================================
   */

  const handleUpdateLegal =
    async () => {
      if (
        editingId === null ||
        !Number.isInteger(
          editingId
        ) ||
        editingId <= 0
      ) {
        alert(
          'Invalid legal document ID.'
        );
        return;
      }

      if (
        !legalForm.title.trim() ||
        !legalForm.code.trim()
      ) {
        alert(
          'Title and legal code are required.'
        );
        return;
      }

      if (
        !legalForm.description.trim()
      ) {
        alert(
          'Description is required.'
        );
        return;
      }

      try {
        setSaving(true);

        const response =
          await fetch(
            `${API_BASE}/update_legal_knowledge.php`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body: JSON.stringify({
                id:
                  editingId,

                document_name:
                  legalForm.title.trim(),

                legal_code:
                  legalForm.code.trim(),

                legal_type:
                  legalForm.type,

                description:
                  legalForm.description.trim(),

                case_types:
                  legalForm.caseTypes,

                status:
                  'active',
              }),
            }
          );

        const result =
          await response.json();

        if (
          !response.ok ||
          !result.success
        ) {
          throw new Error(
            result.error ||
              'Failed to update legal knowledge'
          );
        }

        await createAuditLog(
          'Update',
          'Legal Knowledge',
          `Updated legal document "${legalForm.title.trim()}" (${legalForm.code.trim()})`
        );

        setShowLegalModal(
          false
        );

        resetForm();

        await loadLegalKnowledge();

        showSavedMessage();

      } catch (error) {
        console.error(
          'Failed to update legal knowledge:',
          error
        );

        alert(
          error instanceof Error
            ? error.message
            : 'Failed to update legal knowledge.'
        );

      } finally {
        setSaving(false);
      }
    };

  /*
   * ========================================================
   * DELETE
   * ========================================================
   */

  const handleDeleteLegal =
    async (
      id: number,
      title: string
    ) => {

      console.log(
        'Deleting legal document:',
        {
          id,
          title,
        }
      );

      if (
        !Number.isInteger(id) ||
        id <= 0
      ) {
        alert(
          'This legal document has an invalid database ID. Please refresh the page.'
        );
        return;
      }

      const confirmed =
        window.confirm(
          `Are you sure you want to delete "${title}"?\n\nThis action cannot be undone.`
        );

      if (!confirmed) {
        return;
      }

      try {
        setSaving(true);

        const response =
          await fetch(
            `${API_BASE}/delete_legal_knowledge.php`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body: JSON.stringify({
                id,
              }),
            }
          );

        const result =
          await response.json();

        if (
          !response.ok ||
          !result.success
        ) {
          throw new Error(
            result.error ||
              'Failed to delete legal knowledge'
          );
        }

        await createAuditLog(
          'Delete',
          'Legal Knowledge',
          `Deleted legal document "${title}"`
        );

        /*
         * Immediately remove from UI.
         */
        setLegalList(
          (previous) =>
            previous.filter(
              (document) =>
                document.id !==
                id
            )
        );

        /*
         * Verify with database.
         */
        await loadLegalKnowledge();

        showSavedMessage();

      } catch (error) {
        console.error(
          'Failed to delete legal knowledge:',
          error
        );

        alert(
          error instanceof Error
            ? error.message
            : 'Failed to delete legal knowledge.'
        );

      } finally {
        setSaving(false);
      }
    };

  /*
   * ========================================================
   * SEARCH + FILTER
   * ========================================================
   */

  const filtered =
    legalList.filter(
      (doc) => {
        const searchText =
          search
            .toLowerCase()
            .trim();

        const matchType =
          filterType === 'all' ||
          doc.type ===
            filterType;

        if (!searchText) {
          return matchType;
        }

        const matchSearch =
          (doc.title || '')
            .toLowerCase()
            .includes(
              searchText
            ) ||

          (doc.code || '')
            .toLowerCase()
            .includes(
              searchText
            ) ||

          (doc.description ||
            '')
            .toLowerCase()
            .includes(
              searchText
            ) ||

          (doc.caseTypes || [])
            .some(
              (caseType) =>
                caseType
                  .toLowerCase()
                  .includes(
                    searchText
                  )
            );

        return (
          matchType &&
          matchSearch
        );
      }
    );

  /*
   * ========================================================
   * CASE TYPE TOGGLE
   * ========================================================
   */

  const toggleCaseType =
    (
      caseType: string
    ) => {
      setLegalForm(
        (previous) => ({
          ...previous,

          caseTypes:
            previous.caseTypes.includes(
              caseType
            )
              ? previous.caseTypes.filter(
                  (item) =>
                    item !==
                    caseType
                )
              : [
                  ...previous.caseTypes,
                  caseType,
                ],
        })
      );
    };

  /*
   * ========================================================
   * CLOSE MODAL
   * ========================================================
   */

  const closeModal = () => {
    if (saving) {
      return;
    }

    setShowLegalModal(
      false
    );

    resetForm();
  };

  /*
   * ========================================================
   * UI
   * ========================================================
   */

  return (
    <AppLayout
      title="Legal Knowledge Base"
      breadcrumb={[
        'Admin',
        'Legal Knowledge',
      ]}
    >
      <div className="space-y-6">

        {/* =================================================
            STATISTICS
        ================================================= */}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {[
            {
              label: 'Laws',

              value:
                legalList.filter(
                  (item) =>
                    item.type ===
                    'law'
                ).length,

              color:
                'text-red-600',

              bg:
                'bg-red-50',

              icon:
                'ri-scales-3-line',
            },

            {
              label:
                'Guidelines',

              value:
                legalList.filter(
                  (item) =>
                    item.type ===
                    'guideline'
                ).length,

              color:
                'text-teal-600',

              bg:
                'bg-teal-50',

              icon:
                'ri-file-list-3-line',
            },

            {
              label:
                'Policies',

              value:
                legalList.filter(
                  (item) =>
                    item.type ===
                    'policy'
                ).length,

              color:
                'text-amber-600',

              bg:
                'bg-amber-50',

              icon:
                'ri-shield-check-line',
            },
          ].map(
            (stat) => (
              <div
                key={`stat-${stat.label}`}
                className="bg-white rounded-xl border border-gray-100 p-5 flex items-center gap-4"
              >
                <div
                  className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center flex-shrink-0`}
                >
                  <i
                    className={`${stat.icon} ${stat.color} text-lg`}
                  />
                </div>

                <div>
                  <p
                    className={`text-2xl font-bold ${stat.color}`}
                  >
                    {
                      stat.value
                    }
                  </p>

                  <p className="text-sm text-gray-500">
                    {
                      stat.label
                    }
                  </p>
                </div>
              </div>
            )
          )}

        </div>

        {/* =================================================
            SEARCH / FILTER / ADD
        ================================================= */}

        <div className="flex flex-wrap items-center justify-between gap-3">

          <div className="flex items-center gap-2 flex-wrap">

            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 min-w-48">

              <i className="ri-search-line text-gray-400 text-sm" />

              <input
                value={
                  search
                }
                onChange={(
                  event
                ) =>
                  setSearch(
                    event
                      .target
                      .value
                  )
                }
                placeholder="Search documents..."
                className="bg-transparent text-sm outline-none w-full placeholder-gray-400 text-gray-700"
              />

            </div>

            <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1">

              {(
                [
                  'all',
                  'law',
                  'guideline',
                  'policy',
                ] as const
              ).map(
                (type) => (
                  <button
                    key={`filter-${type}`}
                    type="button"
                    onClick={() =>
                      setFilterType(
                        type
                      )
                    }
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap capitalize ${
                      filterType ===
                      type
                        ? 'bg-teal-500 text-white'
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {type ===
                    'all'
                      ? 'All'
                      : type}
                  </button>
                )
              )}

            </div>
          </div>

          <div className="flex items-center gap-2">

            {legalSaved && (
              <span className="text-xs text-teal-600 bg-teal-50 border border-teal-200 px-3 py-1.5 rounded-lg flex items-center gap-1">
                <i className="ri-checkbox-circle-line" />
                Saved!
              </span>
            )}

            <button
              type="button"
              onClick={
                openAddModal
              }
              disabled={
                saving
              }
              className="bg-teal-500 text-white text-sm px-4 py-2.5 rounded-xl hover:bg-teal-600 cursor-pointer whitespace-nowrap flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-add-line" />

              Add Legal Document
            </button>

          </div>
        </div>

        {/* =================================================
            LOADING
        ================================================= */}

        {loading ? (

          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">

            <i className="ri-loader-4-line animate-spin text-3xl text-teal-500" />

            <p className="text-sm text-gray-500 mt-3">
              Loading legal knowledge...
            </p>

          </div>

        ) : filtered.length === 0 ? (

          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">

            <div className="w-14 h-14 bg-gray-50 rounded-full flex items-center justify-center mx-auto">

              <i className="ri-scales-3-line text-2xl text-gray-400" />

            </div>

            <p className="text-sm font-medium text-gray-700 mt-4">
              No legal documents found
            </p>

            <p className="text-xs text-gray-400 mt-1">
              Add a legal document or change your search/filter.
            </p>

          </div>

        ) : (

          <div className="space-y-3">

            {filtered.map(
              (doc) => (

                <div
                  key={`legal-document-${doc.id}`}
                  className="bg-white rounded-xl border border-gray-100 p-5 hover:border-gray-200 transition-colors"
                >

                  <div className="flex items-start gap-4">

                    <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-scales-3-line text-red-500 text-lg" />
                    </div>

                    <div className="flex-1 min-w-0">

                      <div className="flex items-start justify-between gap-3 mb-2">

                        <div className="min-w-0">

                          <div className="flex items-center gap-2 mb-1 flex-wrap">

                            <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                              {doc.code}
                            </span>

                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                                legalTypeColors[
                                  doc.type
                                ]
                              }`}
                            >
                              {
                                doc.type
                              }
                            </span>

                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                doc.status ===
                                'active'
                                  ? 'bg-teal-100 text-teal-700'
                                  : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {
                                doc.status
                              }
                            </span>

                          </div>

                          <p className="text-sm font-semibold text-gray-800">
                            {
                              doc.title
                            }
                          </p>

                        </div>

                        <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                          Uploaded{' '}
                          {
                            doc.uploadedAt ||
                            '—'
                          }
                        </span>

                      </div>

                      <p className="text-xs text-gray-500 mb-3">
                        {
                          doc.description ||
                          'No description provided.'
                        }
                      </p>

                      <div>

                        <p className="text-xs text-gray-400 font-medium mb-1.5">
                          Mapped to case types:
                        </p>

                        {doc.caseTypes &&
                        doc.caseTypes.length >
                          0 ? (

                          <div className="flex flex-wrap gap-1.5">

                            {doc.caseTypes.map(
                              (
                                caseType,
                                index
                              ) => (

                                <span
                                  key={`case-type-${doc.id}-${index}`}
                                  className="text-xs bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 rounded-full"
                                >
                                  {
                                    caseType
                                  }
                                </span>

                              )
                            )}

                          </div>

                        ) : (

                          <span className="text-xs text-gray-400">
                            No case types mapped
                          </span>

                        )}

                      </div>

                      {/* =================================================
                          ACTIONS
                      ================================================= */}

                      <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-gray-100">

                        <button
                          type="button"
                          onClick={() =>
                            openEditModal(
                              doc
                            )
                          }
                          disabled={
                            saving
                          }
                          className="text-xs font-medium text-teal-600 border border-teal-200 bg-teal-50 hover:bg-teal-100 px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <i className="ri-edit-line" />
                          Edit
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            handleDeleteLegal(
                              doc.id,
                              doc.title
                            )
                          }
                          disabled={
                            saving
                          }
                          className="text-xs font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <i className="ri-delete-bin-line" />
                          Delete
                        </button>

                      </div>

                    </div>

                  </div>

                </div>

              )
            )}

          </div>

        )}

        {/* =================================================
            ADD / EDIT MODAL
        ================================================= */}

        {showLegalModal && (

          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">

            <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">

              <div className="flex items-center justify-between mb-5">

                <h3 className="text-lg font-bold text-gray-900">

                  {editingId !==
                  null
                    ? 'Edit Legal Document'
                    : 'Add Legal Document'}

                </h3>

                <button
                  type="button"
                  onClick={
                    closeModal
                  }
                  disabled={
                    saving
                  }
                  className="text-gray-400 hover:text-gray-600 cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-close-line text-xl" />
                </button>

              </div>

              <div className="space-y-4">

                {/* Title + Code */}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                  <div>

                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Title{' '}
                      <span className="text-red-500">
                        *
                      </span>
                    </label>

                    <input
                      value={
                        legalForm.title
                      }
                      onChange={(
                        event
                      ) =>
                        setLegalForm({
                          ...legalForm,
                          title:
                            event
                              .target
                              .value,
                        })
                      }
                      placeholder="Full title of law/guideline"
                      disabled={
                        saving
                      }
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 disabled:bg-gray-50"
                    />

                  </div>

                  <div>

                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Code{' '}
                      <span className="text-red-500">
                        *
                      </span>
                    </label>

                    <input
                      value={
                        legalForm.code
                      }
                      onChange={(
                        event
                      ) =>
                        setLegalForm({
                          ...legalForm,
                          code:
                            event
                              .target
                              .value,
                        })
                      }
                      placeholder="e.g. RA 7610"
                      disabled={
                        saving
                      }
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 font-mono disabled:bg-gray-50"
                    />

                  </div>

                </div>

                {/* Type */}

                <div>

                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Type
                  </label>

                  <div className="flex gap-2">

                    {(
                      [
                        'law',
                        'guideline',
                        'policy',
                      ] as const
                    ).map(
                      (type) => (

                        <button
                          key={`type-${type}`}
                          type="button"
                          onClick={() =>
                            setLegalForm({
                              ...legalForm,
                              type,
                            })
                          }
                          disabled={
                            saving
                          }
                          className={`flex-1 py-2 rounded-xl border-2 text-sm font-medium capitalize cursor-pointer transition-all disabled:opacity-50 ${
                            legalForm.type ===
                            type
                              ? 'border-teal-500 bg-teal-50 text-teal-700'
                              : 'border-gray-200 text-gray-500 hover:border-gray-300'
                          }`}
                        >
                          {
                            type
                          }
                        </button>

                      )
                    )}

                  </div>

                </div>

                {/* Description */}

                <div>

                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Description{' '}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <textarea
                    value={
                      legalForm.description
                    }
                    onChange={(
                      event
                    ) =>
                      setLegalForm({
                        ...legalForm,
                        description:
                          event
                            .target
                            .value,
                      })
                    }
                    rows={4}
                    maxLength={1000}
                    placeholder="Enter a description of this law, guideline, or policy..."
                    disabled={
                      saving
                    }
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-none disabled:bg-gray-50"
                  />

                  <p className="text-xs text-gray-400 text-right mt-1">
                    {
                      legalForm
                        .description
                        .length
                    }
                    /1000
                  </p>

                </div>

                {/* Case Types */}

                <div>

                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Applicable Case Types
                  </label>

                  <div className="flex flex-wrap gap-2">

                    {caseTypeOptions.map(
                      (caseType) => {

                        const selected =
                          legalForm.caseTypes.includes(
                            caseType
                          );

                        return (
                          <button
                            key={`option-${caseType}`}
                            type="button"
                            onClick={() =>
                              toggleCaseType(
                                caseType
                              )
                            }
                            disabled={
                              saving
                            }
                            className={`text-xs px-3 py-1.5 rounded-full border cursor-pointer transition-all disabled:opacity-50 ${
                              selected
                                ? 'bg-teal-500 text-white border-teal-500'
                                : 'border-gray-200 text-gray-600 hover:border-teal-300'
                            }`}
                          >
                            {
                              caseType
                            }
                          </button>
                        );
                      }
                    )}

                  </div>

                </div>

              </div>

              {/* Footer */}

              <div className="flex gap-3 justify-end mt-6">

                <button
                  type="button"
                  onClick={
                    closeModal
                  }
                  disabled={
                    saving
                  }
                  className="text-sm border border-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={
                    editingId !==
                    null
                      ? handleUpdateLegal
                      : handleAddLegal
                  }
                  disabled={
                    saving ||
                    !legalForm.title.trim() ||
                    !legalForm.code.trim() ||
                    !legalForm.description.trim()
                  }
                  className="text-sm bg-teal-500 text-white px-5 py-2 rounded-xl hover:bg-teal-600 disabled:bg-teal-300 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
                >

                  {saving ? (

                    <>
                      <i className="ri-loader-4-line animate-spin" />

                      Saving...
                    </>

                  ) : (

                    <>
                      <i
                        className={
                          editingId !==
                          null
                            ? 'ri-save-line'
                            : 'ri-add-line'
                        }
                      />

                      {editingId !==
                      null
                        ? 'Save Changes'
                        : 'Add Document'}
                    </>

                  )}

                </button>

              </div>

            </div>

          </div>

        )}

      </div>
    </AppLayout>
  );
}